// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAutoSave } from '../../hooks/useAutoSave';
import { ActivityIndicator, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';
import { card, fieldInput, pill, pillActive, pillText, pillTextActive } from '../../typography';
import SectionHeading from '../shared/SectionHeading';
import PlatformDatePicker from '@/components/PlatformDatePicker';
import { t } from '@/i18n';
import { formatLocalDate, localDateToDate } from '@/services/localDate';
import { formatLastPlayed } from '@/hooks/useRecentCourses';
import { useCourseSearchMissReport } from '@/hooks/useCourseSearchMissReport';
import { useUser } from '@/app/user-context';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { holesCountOptions } from '@/utils/holesCount';
import type { Course, RecentCourse, TourRoundSummary } from '../../types';

// The dropdown sits inline in the editor, so cap how far it can grow; users narrow the
// list by typing rather than scrolling a thousand courses.
const MAX_COURSE_RESULTS = 8;

type Props = {
  round: TourRoundSummary;
  courses: Course[];
  recentCourses?: RecentCourse[];
  loadingCourses: boolean;
  courseError: string | null;
  onAddCourse: () => void;
  onUpdateRoundSettings: (roundId: string, updates: { name?: string; playDate?: string; courseName?: string; course?: Course; holesCount?: number }) => void;
};

export default function RoundSettingsEditor({
  round,
  courses,
  recentCourses = [],
  loadingCourses,
  courseError,
  onAddCourse,
  onUpdateRoundSettings,
}: Props) {
  const { user } = useUser();
  const { isDeveloper } = useEffectiveRoles(user);

  // --- Title ---
  const [titleValue, setTitleValue] = useState(round.name ?? '');

  // --- Date ---
  const [dateValue, setDateValue] = useState(round.playDate ?? '');
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // --- Course search combo ---
  // No location input on purpose: a tour round is planned long before anyone is at the
  // course, so the list is searched by name/city rather than sorted by distance.
  const [courseInput, setCourseInput] = useState(round.courseName ?? '');
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  // Sync when switching round OR when an external update arrives (e.g. round.updated via WS)
  const lastSavedTitleRef = useRef(round.name ?? '');
  const lastSavedDateRef = useRef(round.playDate ?? '');
  const prevRoundIdRef = useRef(round.id);
  useEffect(() => {
    const isNewRound = prevRoundIdRef.current !== round.id;
    if (isNewRound) {
      prevRoundIdRef.current = round.id;
      setTitleValue(round.name ?? '');
      setDateValue(round.playDate ?? '');
      setCourseInput(round.courseName ?? '');
      setSelectedCourseId(null);
      lastSavedTitleRef.current = round.name ?? '';
      lastSavedDateRef.current = round.playDate ?? '';
      setHolesCount(round.holesCount ?? 18);
      return;
    }
    // External update: prop changed but not from our own auto-save
    const incomingTitle = round.name ?? '';
    if (incomingTitle !== lastSavedTitleRef.current) {
      setTitleValue(incomingTitle);
      lastSavedTitleRef.current = incomingTitle;
    }
    const incomingDate = round.playDate ?? '';
    if (incomingDate !== lastSavedDateRef.current) {
      setDateValue(incomingDate);
      lastSavedDateRef.current = incomingDate;
    }
    if (round.courseName && !courseDropdownOpen) {
      setCourseInput(round.courseName);
    }
    if (round.holesCount) {
      setHolesCount(round.holesCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round.id, round.name, round.playDate, round.courseName, round.holesCount]);

  // Auto-save title. The title is optional, so an empty value is a real save
  // (the server maps "" back to null) rather than something to skip.
  useAutoSave(
    titleValue,
    lastSavedTitleRef.current,
    (value) => {
      lastSavedTitleRef.current = value;
      onUpdateRoundSettings(round.id, { name: value });
    },
    { allowEmpty: true }
  );

  // Auto-save date
  useAutoSave(dateValue, lastSavedDateRef.current, (value) => {
    lastSavedDateRef.current = value;
    onUpdateRoundSettings(round.id, { playDate: value });
  });

  // The round only stores the course name, so fall back to a name match to keep the
  // selection visible after a reload or an external round.updated event.
  const selectedCourse = useMemo(
    () =>
      courses.find((c) => c.id === selectedCourseId)
      ?? (round.courseName ? courses.find((c) => c.name === round.courseName) : undefined)
      ?? null,
    [courses, selectedCourseId, round.courseName]
  );

  // Keep input in sync with selected course when dropdown is closed
  useEffect(() => {
    if (selectedCourse && !courseDropdownOpen) {
      setCourseInput(selectedCourse.name);
    }
  }, [selectedCourse, courseDropdownOpen]);

  // Disabled courses must not be selectable at all — drop them before the search sees them.
  const selectableCourses = useMemo(
    () => courses.filter((course) => course.visibility !== 'disabled'),
    [courses]
  );

  const filteredCourses = useMemo(() => {
    const query = courseInput.trim().toLowerCase();
    if (!query) return selectableCourses.slice(0, MAX_COURSE_RESULTS);
    return selectableCourses
      .filter((c) => c.name.toLowerCase().includes(query) || (c.address ?? '').toLowerCase().includes(query))
      .slice(0, MAX_COURSE_RESULTS);
  }, [selectableCourses, courseInput]);

  const showRecents = courseInput.trim().length === 0 && recentCourses.length > 0;

  useCourseSearchMissReport({
    query: courseInput,
    hasResults: filteredCourses.length > 0,
    active: courseDropdownOpen && !loadingCourses && selectableCourses.length > 0,
  });
  // The dropdown always carries the "Add course" action, so it opens even with no matches.
  const showCourseDropdown = courseDropdownOpen;

  useEffect(() => {
    setHighlightIndex(0);
  }, [filteredCourses.length, courseInput]);

  // --- Holes count ---
  const [holesCount, setHolesCount] = useState<number>(round.holesCount ?? 18);

  const handleHolesChange = useCallback((value: number) => {
    setHolesCount(value);
    onUpdateRoundSettings(round.id, { holesCount: value });
  }, [onUpdateRoundSettings, round.id]);

  const selectCourse = useCallback((course: Course) => {
    setSelectedCourseId(course.id);
    setCourseInput(course.name);
    setCourseDropdownOpen(false);
    setHighlightIndex(0);
    // Send full course object + courseName for draft round tracking
    onUpdateRoundSettings(round.id, { courseName: course.name, course });
  }, [onUpdateRoundSettings, round.id]);

  const selectRecentCourse = useCallback((recent: RecentCourse) => {
    // Recently-played courses carry no coordinates; the round already references
    // the course by id/name, so coords are not needed for the settings update.
    selectCourse({ id: recent.id, name: recent.name, address: recent.address, lat: 0, lon: 0 });
  }, [selectCourse]);

  const handleCourseKeyDown = useCallback(
    (e: any) => {
      const key = e.key ?? e.nativeEvent?.key;
      if (!showCourseDropdown) {
        if (key === 'ArrowDown' || key === 'Enter') setCourseDropdownOpen(true);
        return;
      }
      if (key === 'ArrowDown') {
        e.preventDefault?.();
        setHighlightIndex((prev) => Math.min(prev + 1, filteredCourses.length - 1));
      } else if (key === 'ArrowUp') {
        e.preventDefault?.();
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
      } else if (key === 'Enter') {
        e.preventDefault?.();
        const target = filteredCourses[highlightIndex];
        if (target) selectCourse(target);
      } else if (key === 'Escape') {
        setCourseDropdownOpen(false);
      }
    },
    [filteredCourses, highlightIndex, selectCourse, showCourseDropdown]
  );

  return (
    <View style={styles.container}>
      <SectionHeading title="Round Settings" />

      {/* Round Title */}
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>Title (optional)</Text>
        <TextInput
          style={styles.textInput}
          value={titleValue}
          onChangeText={setTitleValue}
          placeholder="e.g. Saturday Morning Round"
          placeholderTextColor={appColors.text.muted}
        />
      </View>

      {/* Play Date */}
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>Play Date</Text>
        <Pressable style={styles.dateButton} onPress={() => setDatePickerOpen(true)}>
          <Text style={dateValue ? styles.dateButtonText : styles.dateButtonPlaceholder}>
            {dateValue || 'Select a date'}
          </Text>
        </Pressable>
        <PlatformDatePicker
          open={datePickerOpen}
          date={localDateToDate(dateValue) ?? new Date()}
          onConfirm={(date) => {
            setDateValue(formatLocalDate(date));
            setDatePickerOpen(false);
          }}
          onCancel={() => setDatePickerOpen(false)}
        />
      </View>

      {/* Course */}
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>Course</Text>

        {loadingCourses && (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" color={appColors.accent.primary} />
            <Text style={styles.loadingText}>Loading courses...</Text>
          </View>
        )}

        {courseError && <Text style={styles.errorText}>{courseError}</Text>}

        <View style={styles.comboWrapper}>
          <View style={[styles.inputRow, showCourseDropdown && styles.inputRowOpen]}>
            <TextInput
              style={styles.comboInput}
              value={courseInput}
              onChangeText={(value) => {
                setCourseInput(value);
                setCourseDropdownOpen(true);
                if (selectedCourseId) setSelectedCourseId(null);
              }}
              placeholder={t('players.courseSearchPlaceholder')}
              placeholderTextColor={appColors.text.muted}
              onFocus={() => setCourseDropdownOpen(true)}
              onBlur={() => {
                setTimeout(() => setCourseDropdownOpen(false), 200);
              }}
              onKeyPress={handleCourseKeyDown}
            />
            {selectedCourse && !courseDropdownOpen && (
              <Text style={styles.selectedBadge}>Selected</Text>
            )}
          </View>

          {showCourseDropdown && (
            <View style={styles.dropdown}>
              <Pressable
                style={styles.addCourseButton}
                onPress={onAddCourse}
                accessibilityLabel={t('players.addCourse')}
              >
                <Text style={styles.addCourseButtonText}>{t('players.addCourse')}</Text>
              </Pressable>
              <Text style={styles.searchHint}>{t('players.searchAnyCourseHint')}</Text>
              {showRecents && (
                <View>
                  <Text style={styles.recentHeader}>
                    {t('players.recentlyPlayedTitle')}
                    <Text style={styles.recentHeaderSub}> · {t('players.recentlyPlayedSubtitle')}</Text>
                  </Text>
                  {recentCourses.map((recent) => {
                    const isSelected = selectedCourse?.id === recent.id;
                    return (
                      <Pressable
                        key={recent.lastRoundId || recent.id}
                        style={[styles.dropdownItem, isSelected && styles.dropdownItemSelected]}
                        onPress={() => selectRecentCourse(recent)}
                      >
                        <View style={styles.courseInfo}>
                          <Text style={[styles.courseName, isSelected && styles.courseNameActive]}>{recent.name}</Text>
                          {recent.address ? <Text style={styles.courseAddress} numberOfLines={1}>{recent.address}</Text> : null}
                        </View>
                        {formatLastPlayed(recent) ? (
                          <Text style={styles.recentMeta}>{formatLastPlayed(recent)}</Text>
                        ) : null}
                      </Pressable>
                    );
                  })}
                  <Text style={styles.recentDivider}>{t('players.lookingForAnother')}</Text>
                </View>
              )}
              {filteredCourses.map((course, index) => {
                const isHighlighted = index === highlightIndex;
                const isSelected = selectedCourse?.id === course.id;
                return (
                  <Pressable
                    key={course.id}
                    style={[
                      styles.dropdownItem,
                      isHighlighted && styles.dropdownItemHighlighted,
                      isSelected && styles.dropdownItemSelected,
                    ]}
                    onPress={() => selectCourse(course)}
                    onHoverIn={() => setHighlightIndex(index)}
                  >
                    <View style={styles.courseInfo}>
                      <Text style={[styles.courseName, (isHighlighted || isSelected) && styles.courseNameActive]}>
                        {course.name}
                      </Text>
                      <Text style={styles.courseAddress} numberOfLines={1}>{course.address}</Text>
                    </View>
                    <Text
                      style={[
                        styles.courseBadge,
                        course.visibility === 'private'
                          ? styles.courseBadgePrivate
                          : course.visibility === 'pending'
                            ? styles.courseBadgePending
                            : styles.courseBadgeOfficial,
                      ]}
                    >
                      {course.visibility === 'private'
                        ? t('players.coursePrivateBadge')
                        : course.visibility === 'pending'
                          ? t('players.coursePendingBadge')
                          : t('players.courseOfficialBadge')}
                    </Text>
                    {isSelected && <Text style={styles.checkmark}>✓</Text>}
                  </Pressable>
                );
              })}
              {filteredCourses.length === 0 && !showRecents && !loadingCourses && (
                <Text style={styles.emptyState}>{t('players.courseSearchEmpty')}</Text>
              )}
            </View>
          )}
        </View>

        {selectedCourse && !courseDropdownOpen && selectedCourse.address ? (
          <View style={styles.detailBox}>
            <Text style={styles.detailAddress}>{selectedCourse.address}</Text>
          </View>
        ) : null}
      </View>

      {/* Holes Count */}
      <View style={styles.field}>
        <Text style={styles.fieldLabel}>Number of Holes</Text>
        <View style={styles.holesRow}>
          {holesCountOptions(isDeveloper).map((count) => (
            <Pressable
              key={count}
              style={[styles.holesOption, holesCount === count && styles.holesOptionSelected]}
              onPress={() => handleHolesChange(count)}
            >
              <Text style={[styles.holesOptionText, holesCount === count && styles.holesOptionTextSelected]}>{count} holes</Text>
            </Pressable>
          ))}
        </View>
      </View>
    </View>
  );
}


const styles = StyleSheet.create({
  container: {
    ...card,
    gap: 16,
  },
  field: {
    gap: 6,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: appColors.text.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingHorizontal: 2,
  },
  textInput: {
    ...fieldInput,
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  dateButton: fieldInput,
  dateButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  dateButtonPlaceholder: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.muted,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 4,
  },
  loadingText: {
    fontSize: 13,
    color: appColors.text.muted,
  },
  errorText: {
    fontSize: 13,
    color: appColors.status.danger,
  },
  // The frame lives on the wrapper, not the input, because the course list attaches underneath as
  // one connected unit. Same treatment as GoffyInput: an accent border stays legible on the card
  // behind it, where the old tonal border was invisible.
  comboWrapper: {
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    borderRadius: 10,
    backgroundColor: appColors.surface.input,
    overflow: 'hidden',
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 10,
  },
  inputRowOpen: {
    borderBottomWidth: 1,
    borderBottomColor: appColors.border.default,
  },
  comboInput: {
    flex: 1,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  selectedBadge: {
    fontSize: 10,
    fontWeight: '700',
    color: appColors.accent.primary,
    backgroundColor: appColors.accent.subtleBg,
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 6,
    overflow: 'hidden',
  },
  dropdown: {
    backgroundColor: appColors.surface.elevated,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: appColors.border.default,
  },
  dropdownItemHighlighted: {
    backgroundColor: appColors.accent.subtleBg,
  },
  dropdownItemSelected: {
    backgroundColor: appColors.accent.subtleBg,
  },
  courseInfo: {
    flex: 1,
    gap: 2,
  },
  courseName: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  courseNameActive: {
    fontWeight: '700',
  },
  courseAddress: {
    fontSize: 12,
    color: appColors.text.muted,
  },
  courseBadge: {
    marginLeft: 8,
    alignSelf: 'center',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    overflow: 'hidden',
  },
  courseBadgeOfficial: {
    color: '#06280F',
    backgroundColor: '#A1D493',
  },
  courseBadgePrivate: {
    color: '#FCD34D',
    backgroundColor: 'rgba(252,211,77,0.15)',
  },
  courseBadgePending: {
    color: '#93C5FD',
    backgroundColor: 'rgba(147,197,253,0.15)',
  },
  searchHint: {
    fontSize: 11,
    color: appColors.text.muted,
    paddingHorizontal: 14,
    paddingTop: 8,
    paddingBottom: 2,
  },
  emptyState: {
    fontSize: 13,
    color: appColors.text.muted,
    paddingHorizontal: 14,
    paddingVertical: 12,
    textAlign: 'center',
  },
  addCourseButton: {
    margin: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.button.secondaryBorder,
    backgroundColor: appColors.button.secondaryBg,
    paddingVertical: 9,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  addCourseButtonText: {
    fontSize: 13,
    fontWeight: '700',
    color: appColors.button.secondaryText,
  },
  checkmark: {
    fontSize: 14,
    fontWeight: '800',
    color: appColors.accent.primary,
    marginLeft: 8,
  },
  recentHeader: {
    fontSize: 11,
    fontWeight: '700',
    color: appColors.accent.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 4,
  },
  recentHeaderSub: {
    fontWeight: '400',
    color: appColors.text.muted,
    textTransform: 'none',
    letterSpacing: 0,
  },
  recentMeta: {
    fontSize: 11,
    color: appColors.text.muted,
    marginLeft: 8,
  },
  recentDivider: {
    fontSize: 11,
    color: appColors.text.muted,
    paddingHorizontal: 14,
    paddingTop: 8,
    paddingBottom: 4,
    borderTopWidth: 1,
    borderTopColor: appColors.border.default,
  },
  detailBox: {
    backgroundColor: appColors.surface.alt,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  detailAddress: {
    fontSize: 12,
    color: appColors.text.muted,
  },
  holesRow: {
    flexDirection: 'row',
    gap: 10,
  },
  holesOption: {
    ...pill,
    alignItems: 'center',
  },
  holesOptionSelected: pillActive,
  holesOptionText: {
    ...pillText,
    fontSize: 14,
  },
  holesOptionTextSelected: {
    ...pillTextActive,
    fontWeight: '700',
  },
});
